/**
 * Decode JWT payload (client-side, no verification)
 * @param {string} token - JWT token string
 * @returns {object} Decoded JWT payload
 * @throws {Error} If token is invalid
 */
export const decodeJWT = (token) => {
    try {
        if (!token || typeof token !== "string") {
            throw new Error("Token must be a valid string");
        }

        const parts = token.split(".");
        if (parts.length !== 3) {
            throw new Error("Invalid JWT format");
        }

        const payload = parts[1];
        // Add padding if needed for base64 decoding
        const paddedPayload =
            payload + "=".repeat((4 - (payload.length % 4)) % 4);
        const decoded = JSON.parse(atob(paddedPayload));

        return decoded;
    } catch (error) {
        throw new Error(`Invalid JWT token: ${error.message}`);
    }
};

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is expired
 */
export const isJWTExpired = (token) => {
    try {
        const decoded = decodeJWT(token);
        if (!decoded.exp) return false;

        const currentTime = Math.floor(Date.now() / 1000);
        return decoded.exp < currentTime;
    } catch (error) {
        return true; // Consider invalid tokens as expired
    }
};

/**
 * Check if JWT token is valid (not expired and properly formatted)
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is valid
 */
export const isJWTValid = (token) => {
    try {
        if (!token || typeof token !== "string") return false;
        const decoded = decodeJWT(token);
        return !isJWTExpired(token) && decoded.user_id;
    } catch (error) {
        return false;
    }
};

/**
 * Extract GoPeople user ID (UUID) from JWT token
 * @param {string} token - JWT token string
 * @returns {string|null} GoPeople user ID (UUID) or null if not found
 */
export const getGoPeopleUserIdFromJWT = (token) => {
    try {
        const decoded = decodeJWT(token);
        return decoded.user_id || null;
    } catch (error) {
        console.error("Error extracting GoPeople user ID from JWT:", error);
        return null;
    }
};

/**
 * Extract TTP user ID (numeric) from JWT token
 * @param {string} token - JWT token string
 * @returns {number|null} TTP user ID (numeric) or null if not found
 */
export const getTTPUserIdFromJWT = (token) => {
    try {
        const decoded = decodeJWT(token);
        return decoded?.apiTtp_token?.user_id || null;
    } catch (error) {
        console.error("Error extracting TTP user ID from JWT:", error);
        return null;
    }
};

/**
 * Extract TTP access token from JWT token
 * @param {string} token - JWT token string
 * @returns {string|null} TTP access token or null if not found
 */
export const getTTPAccessTokenFromJWT = (token) => {
    try {
        const decoded = decodeJWT(token);
        return decoded?.apiTtp_token?.access_token || null;
    } catch (error) {
        console.error("Error extracting TTP access token from JWT:", error);
        return null;
    }
};

/**
 * Extract comprehensive user data from JWT token
 * @param {string} token - JWT token string
 * @returns {object} Object containing all user authentication data
 */
export const extractUserDataFromJWT = (token) => {
    try {
        const decoded = decodeJWT(token);
        return {
            // GoPeople user data
            goPeopleUserId: decoded.user_id || null,
            // Email is stored in jti field in the new token structure
            email: decoded.jti || decoded.email || null,
            phone: decoded.phone || null,

            // TTP API embedded data
            ttpUserId:
                decoded?.apiTtp_token?.user_id || decoded.apittpuserId || null,
            ttpAccessToken: decoded?.apiTtp_token?.access_token || null,
            ttpExpiresIn: decoded?.apiTtp_token?.expires_in || null,
            ttpScope: decoded?.apiTtp_token?.scope || null,

            // User role and organization data
            userType:
                decoded.role || null,
            selectedOrganizationId:
                decoded.selected_organization_id ||
                null,
            roleType:
                decoded?.apiTtp_token?.role_type || null,
            roleTypeStatus:
                decoded?.apiTtp_token?.role_type_status ||
                null,

            // JWT standard claims
            issuer: decoded.iss || null,
            subject: decoded.sub || null,
            expiresAt: decoded.exp || null,
            notBefore: decoded.nbf || null,
            issuedAt: decoded.iat || null,
            jwtId: decoded.jti || null,
        };
    } catch (error) {
        console.error("Error extracting user data from JWT:", error);
        return {
            goPeopleUserId: null,
            email: null,
            phone: null,
            ttpUserId: null,
            ttpAccessToken: null,
            ttpExpiresIn: null,
            ttpScope: null,
            userType: null,
            selectedOrganizationId: null,
            roleType: null,
            roleTypeStatus: null,
            issuer: null,
            subject: null,
            expiresAt: null,
            notBefore: null,
            issuedAt: null,
            jwtId: null,
        };
    }
};

/**
 * Map user type from JWT to TamTam role
 * @param {string} userType - User type from JWT (ADMIN, MANAGER, etc.)
 * @param {string} roleType - Role type from JWT (LEGAL_REPRESENTATIVE, etc.)
 * @param {string} roleTypeStatus - Role type status from JWT (ADMIN, MANAGER, etc.)
 * @returns {string} Mapped TamTam role
 */
export const mapJWTUserTypeToTamTamRole = (
    userType,
    roleType,
    roleTypeStatus
) => {
    // Priority mapping based on user_type and role_type_status
    if (userType === "ADMIN" || userType === "ADMIN_SUPERVISOR" || roleTypeStatus === "ADMIN") {
        return "CHIEF_EDITOR";
    }

    if (userType === "MANAGER" || roleTypeStatus === "MANAGER") {
        return "CHIEF_EDITOR";
    }

    if (userType === "EDITOR" || roleType === "EDITOR") {
        return "REDACTOR";
    }

    if (userType === "AUTHOR" || roleType === "AUTHOR") {
        return "AUTHOR";
    }

    // Default role for authenticated users
    return "GUEST";
};

/**
 * Extract complete authentication context from JWT
 * @param {string} token - JWT token string
 * @returns {object} Complete authentication context
 */
export const extractAuthContextFromJWT = (token) => {
    try {
        const userData = extractUserDataFromJWT(token);
        const mappedRole = mapJWTUserTypeToTamTamRole(
            userData.userType,
            userData.roleType,
            userData.roleTypeStatus
        );

        return {
            ...userData,
            mappedTamTamRole: mappedRole,
            isValid: isJWTValid(token),
            isExpired: isJWTExpired(token),
        };
    } catch (error) {
        console.error("Error extracting auth context from JWT:", error);
        return {
            ...extractUserDataFromJWT(token),
            mappedTamTamRole: "GUEST",
            isValid: false,
            isExpired: true,
        };
    }
};

/**
 * Map GoPeople UUID to email address from JWT context
 * @param {string} goPeopleUuid - GoPeople user UUID
 * @param {string} jwtToken - JWT token containing user data
 * @returns {string|null} Email address or null if not found
 */
export const mapGoPeopleIdentifierToEmail = (goPeopleUuid, jwtToken) => {
    try {
        if (!goPeopleUuid || !jwtToken) {
            return null;
        }

        const authContext = extractAuthContextFromJWT(jwtToken);
        // Check if the provided UUID matches the current user's GoPeople UUID
        if (authContext.goPeopleUserId === goPeopleUuid) {
            return authContext.email;
        }

        // If no match found, return null
        return null;
    } catch (error) {
        console.error("Error mapping GoPeople UUID to email:", error);
        return null;
    }
};

/**
 * Get TTP-compatible user identifier (email) from GoPeople UUID or return as-is if already email
 * @param {string} identifier - User identifier (could be GoPeople UUID or email)
 * @param {string} jwtToken - JWT token containing user data
 * @returns {string} TTP-compatible identifier (email address)
 */
export const getTTPCompatibleUserIdentifier = (identifier, jwtToken) => {
    try {
        if (!identifier) {
            return identifier;
        }

        // Check if identifier is already an email (contains @ symbol)
        if (identifier.includes("@")) {
            return identifier;
        }

        // Check if identifier looks like a UUID (36 characters with hyphens)
        const uuidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(identifier)) {
            // Try to map UUID to email
            const email = mapGoPeopleIdentifierToEmail(identifier, jwtToken);
            return email || identifier; // Return email if found, otherwise return original identifier
        }

        // Return identifier as-is if it's neither email nor UUID
        return identifier;
    } catch (error) {
        console.error("Error getting TTP-compatible user identifier:", error);
        return identifier;
    }
};

// /**
//  * Fetch selected organization data using JWT selected_organization_id
//  * @param {string} jwtToken - JWT token containing selected_organization_id
//  * @param {string} ttpAccessToken - TTP access token for API calls
//  * @returns {object|null} Organization object or null
//  */
// export const fetchSelectedOrganizationFromJWT = async (
//     jwtToken,
//     ttpAccessToken
// ) => {
//     try {
//         const authContext = extractAuthContextFromJWT(jwtToken);

//         if (authContext.selectedOrganizationId && ttpAccessToken) {
//             const { getOrganizationById } = require("api/TamtamAPI/auth");
//             const response = await getOrganizationById(
//                 ttpAccessToken,
//                 authContext.selectedOrganizationId
//             );

//             return response.data?.data?.[0] || null;
//         }

//         return null;
//     } catch (error) {
//         console.error("Error fetching selected organization from JWT:", error);
//         return null;
//     }
// };
